<nav class="sidebar">
  <!-- Brand row — Veritise wordmark + optional Import Agent icon (far
       right). Toggle lives on `.app-body` (far left) so it stays put
       when this rail fully retracts. Import Agent is a start-work
       control (new tab), not a destination — icon-only keeps the
       Harvey brand row quiet; tooltip carries the label. -->
  <div class="sidebar-brand-row">
    <span class="sidebar-brand">{{ appName }}</span>
    @if (canAccessImportAgent()) {
      <a
        [href]="importAgentHref()"
        class="sidebar-import-agent"
        (click)="openImportAgent($event)"
        [matTooltip]="'sidebar.import_agent' | transloco"
        matTooltipPosition="right"
        [attr.aria-label]="'sidebar.import_agent_aria' | transloco"
      >
        <app-action-glyph name="synthesis-external" />
      </a>
    }
  </div>

  <!-- ─── Scrollable nav body — destinations only ─── -->
  <div class="sidebar-scroll">
    @if (isUserSettingsChrome() || isOrgSettingsChrome()) {
      <!-- ─── SETTINGS CHROME ───────────────────────────────────────────
           User and Organisation are separate catalogues (never combined).
           Entered from the org-selector; Back restores product nav. -->
      <div class="sidebar-settings-back">
        <button
          type="button"
          class="sidebar-back-to-workspace"
          [attr.aria-label]="backToWorkspaceAriaLabel()"
          [matTooltip]="backToWorkspaceAriaLabel()"
          matTooltipPosition="right"
          (click)="backToWorkspace()"
        >
          <app-icon iconKey="arrowBack" [showLabel]="false"></app-icon>
          <span class="sidebar-back-to-workspace-label">{{ backToWorkspaceLabel() }}</span>
        </button>
      </div>

      @if (isUserSettingsChrome()) {
        <!-- Single Account destination — no one-item section group. -->
        <ul class="sidebar-section sidebar-section-primary">
          <li>
            <a
              routerLink="/settings"
              routerLinkActive="active"
              [routerLinkActiveOptions]="exactPathActive"
            >
              <app-icon iconKey="settings" [label]="'sidebar.account' | transloco"></app-icon>
            </a>
          </li>
        </ul>
      } @else {
        <app-sidebar-nav-group
          [label]="'sidebar.section.organisation' | transloco"
          [activePaths]="settingsOrgPaths"
        >
          <ul class="sidebar-section">
            <li>
              <a
                routerLink="/organisation"
                routerLinkActive="active"
                [routerLinkActiveOptions]="exactPathActive"
              >
                <app-icon
                  iconKey="organisation"
                  [label]="'sidebar.org_general' | transloco"
                ></app-icon>
              </a>
            </li>
            <li>
              <a routerLink="/organisation/members" routerLinkActive="active">
                <app-icon iconKey="group" [label]="'sidebar.members' | transloco"></app-icon>
              </a>
            </li>
            @if (showBillingNavItem()) {
              <li>
                <a routerLink="/settings/billing" routerLinkActive="active">
                  <app-icon iconKey="billing" [label]="'sidebar.billing' | transloco"></app-icon>
                </a>
              </li>
            }
            <li>
              <a routerLink="/organisation/advanced" routerLinkActive="active">
                <app-icon iconKey="tune" [label]="'sidebar.org_advanced' | transloco"></app-icon>
              </a>
            </li>
            <li>
              <a routerLink="/organisation/support" routerLinkActive="active">
                <app-icon iconKey="support" [label]="'sidebar.support' | transloco"></app-icon>
              </a>
            </li>
          </ul>
        </app-sidebar-nav-group>
      }
    } @else if (!isManagerView()) {
      <!-- ─── CUSTOMER VIEW ─────────────────────────────────────────────
           Customer-facing surfaces the manager works on behalf of the
           selected customer. -->

      <!-- Primary surfaces — Dashboard + Tasks + Context. No section
           label: these read as the "default destinations". Rendered
           unconditionally for entitled and unentitled orgs alike; if
           the plan doesn't include the surface, the destination page
           shows the upsell banner via `subscription-gate`. Hiding
           entries entirely would strand a discoverable product behind
           an invisible gate. -->
      <ul class="sidebar-section sidebar-section-primary">
        <li>
          <a routerLink="/dashboard" routerLinkActive="active">
            <app-icon iconKey="dashboard" [label]="'sidebar.dashboard' | transloco"></app-icon>
          </a>
        </li>
        <li [class.loading]="!isStateReady">
          <a routerLink="/tasks" routerLinkActive="active">
            <app-icon iconKey="tasks" [label]="'sidebar.tasks' | transloco"></app-icon>
          </a>
        </li>
        <li>
          <a routerLink="/context-documents" routerLinkActive="active">
            <app-icon
              iconKey="contextDocuments"
              [label]="'sidebar.context_documents' | transloco"
            ></app-icon>
          </a>
        </li>
      </ul>

      <!-- Compliance cluster. Individual entries render unconditionally;
           destination pages own the upsell UX. Frameworks carries a
           CAPABILITY gate (`framework_read`) — that's a person axis, not a
           feature-availability axis. -->
      <app-sidebar-nav-group
        [label]="'sidebar.section.compliance' | transloco"
        [activePaths]="compliancePaths"
      >
        <ul class="sidebar-section">
          <li>
            <a routerLink="/assessment" routerLinkActive="active">
              <app-icon iconKey="assessment" [label]="'sidebar.assessment' | transloco"></app-icon>
            </a>
          </li>
          <li>
            <a routerLink="/controls" routerLinkActive="active">
              <app-icon iconKey="controls" [label]="'sidebar.controls' | transloco"></app-icon>
            </a>
          </li>
          @if (canAccessIncidents()) {
            <li>
              <a routerLink="/incidents" routerLinkActive="active">
                <app-icon iconKey="incidents" [label]="'sidebar.incidents' | transloco"></app-icon>
              </a>
            </li>
          }
          <!-- Frameworks is gated on `framework_read`, the same capability the
               route's `frameworksGuard` checks — never on org type, which used
               to hide it from every STANDARD org. Feature-availability is NOT
               gated here; the page handles the upsell if the plan lacks it. -->
          @if (canAccessFrameworks()) {
            <li [class.loading]="!isStateReady">
              <a routerLink="/frameworks" routerLinkActive="active">
                <app-icon
                  iconKey="frameworks"
                  [label]="'sidebar.frameworks' | transloco"
                ></app-icon>
              </a>
            </li>
          }
          <li [class.loading]="!isStateReady">
            <a routerLink="/documents" routerLinkActive="active">
              <app-icon iconKey="documents" [label]="'sidebar.documents' | transloco"></app-icon>
            </a>
          </li>
        </ul>
      </app-sidebar-nav-group>

      <!-- Vendors cluster — the section label is the parent. The items
           (Inventory / Activities / Lists / Registry / TIA) sit as
           sibling top-level destinations underneath it. -->
      @if (canAccessVendors()) {
        <app-sidebar-nav-group
          [label]="'sidebar.section.vendors' | transloco"
          [activePaths]="vendorsPaths"
        >
          <ul class="sidebar-section">
            <li [class.loading]="!isStateReady">
              <a routerLink="/vendors/inventory" routerLinkActive="active">
                <app-icon
                  iconKey="inventory"
                  [label]="'sidebar.vendors_inventory' | transloco"
                ></app-icon>
              </a>
            </li>
            <li>
              <a routerLink="/vendors/activities" routerLinkActive="active">
                <app-icon
                  iconKey="processingActivities"
                  [label]="'sidebar.vendors_activities' | transloco"
                ></app-icon>
              </a>
            </li>
            <li>
              <a routerLink="/vendors/lists" routerLinkActive="active">
                <app-icon
                  iconKey="vendorLists"
                  [label]="'sidebar.vendors_lists' | transloco"
                ></app-icon>
              </a>
            </li>
            <!-- Registry — global vendor catalogue. Read-only for standard
                 users (browse + "Request a new vendor" CTA); super-managers
                 see the same page with write affordances. Route is
                 `viewMode: 'preserve'`, so clicking from here keeps the
                 user in customer view and this active-state highlight
                 stays lit. -->
            <li>
              <a routerLink="/vendors/registry" routerLinkActive="active">
                <app-icon
                  iconKey="vendors"
                  [label]="'sidebar.vendors_registry' | transloco"
                ></app-icon>
              </a>
            </li>
            <!-- TIA register — every open/approved transfer-impact
                 assessment for the effective org, with All / Needs
                 reconfirm (STALE) / Not started tabs (the last one folds
                 in the former standalone "TIA gaps" route). -->
            <li>
              <a routerLink="/vendors/tia-register" routerLinkActive="active">
                <app-icon
                  iconKey="lifecycle"
                  [label]="'sidebar.vendors_tia_register' | transloco"
                ></app-icon>
              </a>
            </li>
          </ul>
        </app-sidebar-nav-group>
      }

      <!-- Trust Centre — the public compliance surface the customer
           embeds on their own website. Gated on the platform-wide
           `hasFeature('trust_center')` primitive so it hides for plans
           that don't include it, and shows on any org where the
           feature is granted (plan default OR SUPER_MANAGER override). -->
      @if (canAccessTrustCenter()) {
        <app-sidebar-nav-group
          [label]="'sidebar.section.trust_center' | transloco"
          [activePaths]="trustCenterPaths"
        >
          <ul class="sidebar-section">
            <li>
              <a routerLink="/trust-center" routerLinkActive="active">
                <app-icon
                  iconKey="trustCenter"
                  [label]="'sidebar.trust_center' | transloco"
                ></app-icon>
              </a>
            </li>
          </ul>
        </app-sidebar-nav-group>
      }
    } @else {
      <!-- ─── MANAGER VIEW ──────────────────────────────────────────────
           Manager-private surfaces the customer cannot see. -->

      <!-- Primary: manager's own tasks. -->
      <ul class="sidebar-section sidebar-section-primary">
        <li>
          <a routerLink="/tasks" routerLinkActive="active">
            <app-icon iconKey="tasks" [label]="'sidebar.tasks' | transloco"></app-icon>
          </a>
        </li>
      </ul>

      <!-- `canReviewAssessments()`, not raw org-type authority: a restricted
           member of the platform org can hold `assessment_review` on
           their own member row and reach this queue with no grant involved. -->
      @if (canReviewAssessments()) {
        <app-sidebar-nav-group
          [label]="'sidebar.section.for_review' | transloco"
          [activePaths]="forReviewPaths"
        >
          <ul class="sidebar-section">
            <li>
              <a routerLink="/assessments-for-review" routerLinkActive="active">
                <app-icon
                  iconKey="assessment"
                  [label]="'sidebar.assessments_for_review' | transloco"
                ></app-icon>
              </a>
            </li>
          </ul>
        </app-sidebar-nav-group>
      }

      <!-- Vendors cluster (manager-view): Registry + Inbox. Gated on
           `canManageVendorRegistry()` (SUPER_MANAGER org + ADMIN or
           VENDOR_MANAGER role) — mirrors the BE capability gate on
           every write to `/v1/vendors[...]`. SM-org MEMBERs see no
           entry; deep-link reads to /vendors/registry still render
           the page without write affordances. -->
      @if (canManageVendorRegistry()) {
        <app-sidebar-nav-group
          [label]="'sidebar.section.vendors' | transloco"
          [activePaths]="vendorsPaths"
        >
          <ul class="sidebar-section">
            <li>
              <a routerLink="/vendors/registry" routerLinkActive="active">
                <app-icon
                  iconKey="vendors"
                  [label]="'sidebar.vendors_registry' | transloco"
                ></app-icon>
              </a>
            </li>
            <li>
              <a routerLink="/vendors/inbox" routerLinkActive="active">
                <app-icon iconKey="inbox" [label]="'sidebar.vendor_inbox' | transloco"></app-icon>
              </a>
            </li>
            <li>
              <a routerLink="/vendors/country-risk" routerLinkActive="active">
                <app-icon
                  iconKey="decisions"
                  [label]="'sidebar.country_risk' | transloco"
                ></app-icon>
              </a>
            </li>
            <li>
              <a routerLink="/vendors/adequacy" routerLinkActive="active">
                <app-icon iconKey="frameworks" [label]="'sidebar.adequacy' | transloco"></app-icon>
              </a>
            </li>
          </ul>
        </app-sidebar-nav-group>
      }

      <app-sidebar-nav-group
        [label]="'sidebar.section.customers' | transloco"
        [activePaths]="customersPaths"
      >
        <ul class="sidebar-section">
          <li>
            <a routerLink="/customers" routerLinkActive="active">
              <app-icon iconKey="customers" [label]="'sidebar.customers' | transloco"></app-icon>
            </a>
          </li>
        </ul>
      </app-sidebar-nav-group>
    }
  </div>

  <!-- Footer — identity only. Urgency (plan) then workspace.
       Import Agent is NOT here: burying a work entry next to the org
       picker demotes it to settings-adjacent chrome. -->
  <div class="sidebar-footer">
    @if (isAdmin === true && showSubscriptionWidget()) {
      <app-sidebar-plan-widget
        [message]="subscriptionWidgetMessage()"
        [actionText]="subscriptionWidgetActionText()"
        (action)="navigateToBilling()"
      ></app-sidebar-plan-widget>
    }
    <app-sidebar-header appearance="rail" />
  </div>
</nav>
